import type { LocalizedString } from 'typesafe-i18n';
import { LL } from 'i18n/i18n.service';
import { AuthService } from 'services/auth.service';
import { showOtherAccountAlertAsync, showStoreRefusedAlertAsync } from './other-account-alert';
import { getMyApp } from 'app/app-interface';
import { LoggingService } from 'services/logging.service';
import { AlertController, AlertButtonRole } from 'providers/alert-controller';
import { getCapacitorPlatform } from 'environments/environment-helpers';
import { IAPService, IAPProduct, IAPPurchaseResult } from 'services/iap.service';
import { AppEnvironment } from 'environments/environment';
import { Capacitor } from '@capacitor/core';
import { OnlineStateService } from 'services/online-state.service';
import { UserDb } from 'services/user-db.service';
import { ImageUploadOutboxService } from 'services/image-upload-outbox.service';
import { confirmSignOutSafeAsync } from 'services/sign-out-guard-flow';
import { describeError } from '@cf-shared/error-describe';
import { shouldReportAutoRestoreError } from './subscribe-restore-error-filter';
import { PurchaseOutcomeMessage, decidePurchaseOutcomeMessage } from './purchase-outcome';
import { resolveNativeSubscribeView, NativeSubscribeView } from './subscribe-store-availability';

/** What the rail borrows from the page it is rendering inside. */
export interface NativeRailHost {
  /**
   * True when the app is running inside Capacitor on a phone rather than in a
   * browser.
   *
   * A plain value rather than a method, unlike everything else here, because the
   * page resolves it once in its constructor and it never changes afterwards. The
   * others below CAN change after the rail is built, which is why they are methods.
   */
  readonly isNativePlatform: boolean;
  /**
   * True once the page's view has been torn down (the NG0911 guard).
   *
   * A method rather than a property because the answer changes while the rail is
   * mid-await, and because the page supplies these as arrow functions closing
   * over itself.
   */
  isViewGone(): boolean;
  /** Mark the page dirty — the app is zoneless, so a model change alone repaints nothing. */
  markPageForCheck(): void;
  /** Show or hide the page's price-loading spinner. */
  setLoadingPrices(loading: boolean): void;
  /** Read that same spinner back; the no-store-products rule needs to know. */
  isLoadingPrices(): boolean;
  /**
   * True when this user's membership is billed on a channel that cannot be
   * managed from here. Resolved during page entry, AFTER this rail is built, so
   * it must be asked for each time rather than captured once.
   */
  hasCrossPlatformSubscription(): boolean;
  /** True for a licence that never expires. Resolved during page entry, as above. */
  hasUnlimitedLicense(): boolean;
  /**
   * Point the page's shared plan-selector at this index.
   *
   * Write-only on purpose. The index belongs to the page because BOTH radio
   * groups in the template — this rail's plan list and the web rails' — bind it
   * two-way, which means the template itself writes it. The rail never reads it
   * back; where it needs the value it keeps its own local.
   */
  setSelectedPlan(index: number): void;
}

/** The services the rail calls out to. The page injects them and hands them over. */
export interface NativeRailServices {
  auth: AuthService;
  logging: LoggingService;
  alertCtrl: AlertController;
  iapService: IAPService;
  // onlineState, userDb and imageUploadOutbox are here for the sign-out offered by
  // the already-linked alert: confirmSignOutSafeAsync needs all of them to decide
  // whether signing out would discard unsynced local work.
  onlineState: OnlineStateService;
  userDb: UserDb;
  imageUploadOutbox: ImageUploadOutboxService;
}

/** A title and message for a one-OK-button alert, as a translation group supplies them. */
interface OkAlertCopy {
  title(): LocalizedString;
  message(): LocalizedString;
}

/**
 * What a purchase and a restore do differently with the same store outcome.
 * Everything not listed here is handled identically for both.
 */
interface StoreFlowReactions {
  /** The silent-problem name reported when the store refuses our confirmation request. */
  storeRefusedProblem: string;
  /** Shown when the store took the purchase and we could not confirm it. */
  notYetConfirmedAlert: OkAlertCopy;
  /** When set, the raw error is written to the console under this prefix in that case. */
  notYetConfirmedLogPrefix: string | undefined;
  /** Shown when the purchase or restore genuinely did not happen. */
  failedAlert: OkAlertCopy;
  /** When set, a failure that carries an error writes it to the console under this prefix. */
  failedLogPrefix: string | undefined;
  /** Whether a failure carrying no error at all still shows the failed alert. */
  showFailedAlertWithoutError: boolean;
}

/**
 * The native Apple App Store / Google Play rail of the subscribe page, split out
 * of the page class (deferred finding `df-f3f664504bca`, ticket
 * [#5241](https://github.com/cybsol/mh-physique/issues/5241) — the third and last
 * of that page's three payment rails to move).
 *
 * ## Why this is a plain class and not an Angular service
 *
 * The same reason the two web rails are (see `subscribe-paddle-rail.ts`):
 * everything here belongs to ONE visit to the subscribe page — which products the
 * store returned, which one the buyer picked, whether a purchase is in flight. An
 * `@Injectable()` service is a singleton, so that state would survive the page and
 * leak into the next visit. The page constructs one of these and drops it with
 * itself.
 *
 * ## What it does NOT own
 *
 * Everything the web rails also touch stays on the page: `isNativePlatform` (the
 * branch selector, the native twin of `webProvider`), the price-loading spinner,
 * the selected-plan index that both radio groups bind two-way, and the
 * already-covered flags that feed the shared `canOfferPurchase` rule. The rail
 * reaches those through {@link NativeRailHost} rather than owning them.
 *
 * ## Nothing in this repository can test this file
 *
 * The end-to-end suite runs in a browser, so it never drives a real store
 * purchase. There is no `TestBed` anywhere in `client/src`, so an Angular page
 * class cannot be unit-tested either. And no test here runs the native billing
 * code (`client/ios-overlay/App/App/BillingPlugin.swift`,
 * `client/android-overlay/app/src/main/java/app/mhphysique/fitness/BillingPlugin.java`).
 * The worst way to get this wrong is a buyer charged by Apple or Google and never
 * granted access, so a change here wants a person who can watch a sandbox purchase
 * on a real device. `design-docs/paddle-integration.md` says the same under "Where
 * each rail's code lives, and the copy beside them".
 */
export class SubscribeNativeRail {
  /**
   * True on iPhone/iPad specifically, as opposed to Android. Gates the storefront
   * listener, the Apple legal copy, and the Restore Purchases button. Fixed for
   * the life of the app run, like the host's `isNativePlatform`.
   */
  readonly isIOSNative: boolean;

  // what the store returned, cheapest first. Empty until the price fetch runs,
  // and legitimately still empty afterwards when the store has nothing to sell
  // us — which is the case the no-store-products message exists for.
  private products: IAPProduct[] = [];
  // the product the Subscribe button would buy. Undefined until the fetch picks one.
  private selectedProduct: IAPProduct = undefined;
  // true while a purchase or a restore is in flight, so the template can disable
  // both buttons and show the spinner.
  private purchaseRunning = false;

  // unsubscribe handle for the late-storefront-change listener registered
  // in fetchPricesAsync. The initial-fetch path uses
  // `getProductsWithSettledStorefrontAsync` which has its own internal
  // settle window; this listener catches storefront resolutions that
  // happen AFTER the page has already rendered (e.g. user taps Subscribe,
  // sandbox-account sheet engages, storefront updates, user cancels and
  // returns to the page — the price should now reflect the resolved
  // sandbox tester's region).
  private storefrontListenerUnsub?: () => void;

  constructor(
    private readonly services: NativeRailServices,
    private readonly host: NativeRailHost
  ) {
    this.isIOSNative = this.host.isNativePlatform && Capacitor.getPlatform() === 'ios';
  }

  /** The plans the store offered, for the template's native plan list. */
  get iapProducts(): IAPProduct[] {
    return this.products;
  }

  /** The plan the Subscribe button would buy, for the template's button label. */
  get selectedIAPProduct(): IAPProduct {
    return this.selectedProduct;
  }

  /** True while a purchase or restore is running, so the template can disable the buttons. */
  get purchaseInProgress(): boolean {
    return this.purchaseRunning;
  }

  /**
   * Leaving the page: detach the storefront-change listener registered in
   * {@link fetchPricesAsync}, so a later re-fetch does not fire against a
   * destroyed view (any markForCheck after teardown would be an NG0911 guard
   * violation).
   */
  teardown(): void {
    this.storefrontListenerUnsub?.();
    this.storefrontListenerUnsub = undefined;
  }

  // #3969: true when the native store finished loading but returned no
  // purchasable products AND the user actually needs to buy here (not already
  // covered by a web sub or an unlimited license). The template shows an
  // actionable "couldn't load options" message in that state instead of a blank
  // paywall. Delegates to the pure resolveNativeSubscribeView so the rule — and
  // the coverage guard — are unit-tested in one place.
  shouldShowNoStoreProductsMessage(): boolean {
    return this.resolveNativeView() === NativeSubscribeView.NoStoreProducts;
  }

  // the single native-view decision, fed from current page state. Used by both
  // the template gate above and the Sentry report below so they never disagree.
  private resolveNativeView(): NativeSubscribeView {
    return resolveNativeSubscribeView({
      isNativePlatform: this.host.isNativePlatform,
      loadingPrices: this.host.isLoadingPrices(),
      productCount: this.products.length,
      hasCrossPlatformSubscription: this.host.hasCrossPlatformSubscription(),
      hasUnlimitedLicense: this.host.hasUnlimitedLicense(),
    });
  }

  // #3969: report the "store returned no products" dead end to Sentry, but only
  // when the user actually hit it (native, done loading, zero products, and not
  // already covered). Gating on the same rule as the message keeps the metric
  // from being inflated by covered / unlimited users. Called from both the
  // initial fetch and the iOS storefront refresh so either path that lands on
  // an empty store is visible to us.
  private reportNoStoreProductsIfNeeded(productIds: string[]) {
    if (this.resolveNativeView() !== NativeSubscribeView.NoStoreProducts) {
      return;
    }
    this.services.logging.trackSilentProblem('SubscribeNoStoreProducts', {
      platform: getCapacitorPlatform(),
      productIds: productIds.join(','),
    });
  }

  async fetchPricesAsync() {
    this.host.setLoadingPrices(true);
    this.host.markPageForCheck();

    let productIds: string[];
    if (this.isIOSNative) {
      productIds = AppEnvironment.appleProductIds || [];
    } else {
      const gpId = AppEnvironment.googlePlayProductId;
      productIds = gpId ? [gpId] : [];
    }
    // initial fetch waits up to ~1.5s for a late `Storefront.updates`
    // event so the first-render price already reflects the user's
    // resolved storefront (no USD-then-EUR flicker for sandbox testers
    // whose region is detected late by StoreKit).
    this.products = await this.services.iapService.getProductsWithSettledStorefrontAsync(productIds);
    if (this.host.isViewGone()) return;

    // sort by price ascending (monthly first, then yearly)
    this.products.sort((a, b) => a.priceMicros - b.priceMicros);

    // preselect the cheapest plan (monthly)
    if (this.products.length > 0) {
      this.host.setSelectedPlan(0);
      this.selectedProduct = this.products[0];
    }

    this.host.setLoadingPrices(false);
    this.host.markPageForCheck();

    // #3969: if the store returned no purchasable products, report the dead end
    // to Sentry. Runs after loadingPrices is cleared so the view resolves to
    // NoStoreProducts (not Loading); the helper also skips covered / unlimited
    // users so the metric measures the real "free user stuck on a dead end" case.
    this.reportNoStoreProductsIfNeeded(productIds);

    // register a persistent listener for storefront changes that happen
    // AFTER initial render (e.g. user enters Apple's purchase sheet which
    // engages a different sandbox tester, then cancels and returns).
    // Cleanup happens in the page's ionViewWillLeave via this.teardown().
    if (this.isIOSNative && !this.storefrontListenerUnsub) {
      this.storefrontListenerUnsub = this.services.iapService.onStorefrontChanged(() => {
        // the callback returns a Promise we can't await (the listener
        // interface is fire-and-forget) — attach a .catch so a StoreKit
        // failure mid-session surfaces to Sentry instead of vanishing as
        // an unhandled rejection
        this.refreshPricesOnStorefrontChange(productIds).catch((err) => {
          this.services.logging.trackSilentProblem('SubscribeRefreshOnStorefrontChangeFailed', {
            errorMessage: describeError(err),
          });
        });
      });
    }

    // proactively check if the store already has a purchase for this app
    await this.checkExistingStorePurchase();
  }

  // re-fetch products after StoreKit reports a storefront change, then
  // re-render the plan cards with the new (correctly-localized) prices.
  // Preserves the user's current plan selection across the refresh so
  // tapping Subscribe doesn't unexpectedly fire against a different plan.
  private async refreshPricesOnStorefrontChange(productIds: string[]) {
    if (this.host.isViewGone()) return;
    console.log('[Subscribe] re-fetching prices after storefront change');
    const refreshed = await this.services.iapService.getProductsAsync(productIds);
    if (this.host.isViewGone()) return;
    refreshed.sort((a, b) => a.priceMicros - b.priceMicros);
    this.products = refreshed;
    // preserve current selection by basePlanId if possible; fall back to first
    if (this.products.length > 0) {
      const currentBasePlanId = this.selectedProduct?.basePlanId;
      const matchIdx = currentBasePlanId
        ? this.products.findIndex(p => p.basePlanId === currentBasePlanId)
        : -1;
      // kept in a local rather than read back off the page: the page owns the
      // index and this rail only ever writes it.
      const selectedIndex = matchIdx >= 0 ? matchIdx : 0;
      this.host.setSelectedPlan(selectedIndex);
      this.selectedProduct = this.products[selectedIndex];
    }
    this.host.markPageForCheck();

    // #3969: a storefront change can drop the product list to zero (e.g. the
    // resolved region has no products). The template already surfaces the
    // message via shouldShowNoStoreProductsMessage(); mirror the initial-fetch
    // path and report it to Sentry too, so this route to the dead end is also
    // visible to us.
    this.reportNoStoreProductsIfNeeded(productIds);
  }

  private async checkExistingStorePurchase() {
    // nobody is signed in, so there is no account to attach a store purchase to:
    // the server call needs a signed-in user and could only throw, filing a false
    // "IAP restore error" (#6506). the user-tapped Restore Purchases button still
    // reports that failure. after sign-in a fresh page runs this check again.
    if (!this.services.auth.user) return;

    const result = await this.services.iapService.restorePurchasesAsync();
    if (this.host.isViewGone()) return;

    if (result.success) {
      // existing purchase restored — user is now subscribed
      void getMyApp().gotoDefaultPageAsync();
      return;
    }

    // Note: we deliberately do NOT surface the `already_linked` error
    // from this proactive auto-restore. A fresh-signup user has
    // AuthRole.Unknown, and app.component.getPageByWelcomePhase()
    // returns ISubscribePage for that role — so any navigation
    // away (or any code path that calls gotoDefaultPageAsync, e.g.
    // the prior Cancel handler's setRootAsync) bounces them right
    // back onto the subscribe page, ngOnInit re-fires, auto-restore
    // re-runs, alert re-appears. Loop. The alert IS shown when the
    // user explicitly taps Restore Purchases — that's an action they
    // initiated, so the trip to Apple Settings or sign-out is an
    // intended response, not a forced loop.
    // `No existing purchases found.` is the expected outcome for a
    // user who has no Apple sub on the device. Google Play's
    // `Subscription is not active (state: SUBSCRIPTION_STATE_*)`
    // family is the equivalent for a Play user whose previous sub is
    // in any terminal state. Both fall through silently and show the
    // subscribe page normally. Any other error code (Store transport
    // failure, JWS decode error, etc.) is unexpected — surface to
    // Sentry via trackSilentProblem so the rate is visible without
    // showing the user a toast on the paywall. The whitelist itself
    // lives in subscribe-restore-error-filter.ts so it can be
    // unit-tested without bootstrapping the page.
    // a failure that threw was already reported by the IAP service itself, with
    // its stack, so only store answers are reported here
    if (shouldReportAutoRestoreError(result)) {
      this.services.logging.trackSilentProblem('SubscribeAutoRestoreError', {
        code: result.error,
      });
    }
  }

  private async showAlreadyLinkedAlert(linkedEmail?: string) {
    const email = linkedEmail || LL.subscribe.alerts.alreadyLinked.unknownAccount();
    const shouldSignOut = await this.services.alertCtrl.showAlertAsync<boolean>({
      title: LL.subscribe.alerts.alreadyLinked.title(),
      message: LL.subscribe.alerts.alreadyLinked.message({ email }),
      buttons: [
        {
          text: LL.common.buttons.cancel(),
          role: AlertButtonRole.Cancel,
          result: false,
        },
        {
          text: LL.subscribe.alerts.alreadyLinked.signOut(),
          role: AlertButtonRole.Primary,
          result: true,
        },
      ],
    });
    if (this.host.isViewGone()) return;
    if (shouldSignOut) {
      // F1 (#3988): run the data-safety guard before signing out, so an offline
      // sign-out with unsynced local writes warns instead of silently discarding.
      const canProceed = await confirmSignOutSafeAsync({
        onlineState: this.services.onlineState,
        userDbWrapper: this.services.userDb?.database ?? null,
        alertCtrl: this.services.alertCtrl,
        logging: this.services.logging,
        imageUploadOutbox: this.services.imageUploadOutbox,
      });
      if (canProceed) {
        // eslint-disable-next-line no-restricted-syntax -- guarded call site approved by the F1 guard flow (confirmSignOutSafeAsync above)
        await this.services.auth.signOutAsync();
      }
    }
    // Cancel: just dismiss the alert and leave the user where they
    // were (typically the subscribe page they were browsing). The
    // earlier setRootAsync(ISettingsPage) was a heuristic escape
    // hatch from the auto-restore loop, no longer needed now that
    // the auto-restore at page entry doesn't surface this alert.
  }

  handleNativeSelect(i: number) {
    this.host.setSelectedPlan(i);
    this.selectedProduct = this.products[i];
    this.host.markPageForCheck();
  }

  handleNativePurchase() {
    if (!this.selectedProduct) return;
    const product = this.selectedProduct;
    this.runStoreFlow(() => this.services.iapService.purchaseAsync(product), {
      storeRefusedProblem: 'IapPurchaseStoreRefused',
      // the store charged them and our confirmation got no answer, so saying
      // the purchase failed would be false (#4651)
      notYetConfirmedAlert: LL.subscribe.alerts.purchaseNotYetConfirmed,
      notYetConfirmedLogPrefix: 'IAP purchase not confirmed:',
      failedAlert: LL.subscribe.alerts.purchaseFailed,
      failedLogPrefix: 'IAP purchase error:',
      showFailedAlertWithoutError: true,
    });
  }

  handleRestorePurchases() {
    this.runStoreFlow(() => this.services.iapService.restorePurchasesAsync(), {
      // the store handed us a purchase and then refused to confirm it, so
      // restoreFailed's "No existing purchases found" would be false (#6243)
      storeRefusedProblem: 'IapRestoreStoreRefused',
      // the store DID hand us a purchase and we could not finish confirming
      // it, so the restoreFailed wording ("No existing purchases found") would
      // be false here (#4651)
      notYetConfirmedAlert: LL.subscribe.alerts.restoreNotYetConfirmed,
      notYetConfirmedLogPrefix: undefined,
      failedAlert: LL.subscribe.alerts.restoreFailed,
      failedLogPrefix: undefined,
      // if a failure ever arrives carrying no explanation at all, stay silent
      // rather than alarm the user — the behaviour restore has always had
      showFailedAlertWithoutError: false,
    });
  }

  /**
   * Runs one store call (a purchase or a restore) and reacts to its outcome.
   * Both buttons share this so a new `PurchaseOutcomeMessage` is handled in one
   * place; only what genuinely differs between them comes in `reactions`.
   */
  private runStoreFlow(operation: () => Promise<IAPPurchaseResult>, reactions: StoreFlowReactions) {
    if (this.purchaseRunning) return;

    this.purchaseRunning = true;
    this.host.markPageForCheck();

    void (async () => {
      try {
        const result = await operation();
        if (this.host.isViewGone()) return;

        const message = decidePurchaseOutcomeMessage(result);
        switch (message) {
          case PurchaseOutcomeMessage.None:
            // succeeded, or the user backed out of the store sheet
            if (result.success) void getMyApp().gotoDefaultPageAsync();
            break;
          case PurchaseOutcomeMessage.AlreadyLinked:
            await this.showAlreadyLinkedAlert(result.linkedEmail);
            break;
          case PurchaseOutcomeMessage.OtherAccount:
            await showOtherAccountAlertAsync(this.services.alertCtrl);
            break;
          case PurchaseOutcomeMessage.StoreRefused:
            // the store refuses this token every time, so "try again" would
            // be advice that cannot work (#6243)
            this.services.logging.trackSilentProblem(reactions.storeRefusedProblem, { error: result.error });
            await showStoreRefusedAlertAsync(this.services.alertCtrl);
            break;
          case PurchaseOutcomeMessage.ConfirmationPending:
            // one OK button and no Try Again: during a store outage a retry
            // fails again at once, which turns a calm message into a loop
            if (reactions.notYetConfirmedLogPrefix) {
              console.error(reactions.notYetConfirmedLogPrefix, result.error);
            }
            await this.showOkAlertAsync(reactions.notYetConfirmedAlert);
            break;
          case PurchaseOutcomeMessage.Failed:
            if (result.error && reactions.failedLogPrefix) {
              console.error(reactions.failedLogPrefix, result.error);
            }
            if (result.error || reactions.showFailedAlertWithoutError) {
              await this.showOkAlertAsync(reactions.failedAlert);
            }
            break;
          default: {
            // a switch over the enum rather than a lookup, and this default is
            // what makes it exhaustive: the repo's switch-exhaustiveness lint
            // rule is off, so without it a new outcome would compile here and
            // show the buyer nothing
            const _exhaustive: never = message;
            void _exhaustive;
          }
        }
      } finally {
        if (!this.host.isViewGone()) {
          this.purchaseRunning = false;
          this.host.markPageForCheck();
        }
      }
    })();
  }

  /** Shows an alert with the given title and message and a single OK button. */
  private async showOkAlertAsync(copy: OkAlertCopy) {
    await this.services.alertCtrl.showAlertAsync({
      title: copy.title(),
      message: copy.message(),
      buttons: [{
        text: LL.common.buttons.ok(),
        role: AlertButtonRole.Primary,
        result: null
      }]
    });
  }

  // Format an ISO 8601 duration like "P14D" / "P2W" / "P1M" / "P1Y"
  // into a localized, pluralized trial length. Both Apple StoreKit and
  // Google Play Billing accept D / W / M / Y units in trial offers
  // (Apple's picker includes 1 week / 2 weeks specifically). Returns
  // empty string on unrecognized input; the trialDisclosureFor wrapper
  // surfaces such cases to Sentry and hides the disclosure line.
  private formatTrialPeriod(iso: string | undefined): string {
    if (!iso) return '';
    const match = /^P(\d+)([DWMY])$/.exec(iso);
    if (!match) return '';
    const count = Number(match[1]);
    switch (match[2]) {
      case 'D': return LL.subscribe.trialPeriod.days({ count });
      case 'W': return LL.subscribe.trialPeriod.weeks({ count });
      case 'M': return LL.subscribe.trialPeriod.months({ count });
      case 'Y': return LL.subscribe.trialPeriod.years({ count });
      default: return '';
    }
  }

  // Localized recurrence ("month" / "year") for the per-plan trial
  // disclosure's "/recurrence" suffix. Returns empty for any other
  // numMonths; the trialDisclosureFor wrapper hides the whole line in
  // that case (and reports to Sentry) so we never render a dangling
  // "$X/" with no recurrence word.
  private recurrenceLabel(numMonths: number): string {
    if (numMonths === 12) return LL.subscribe.recurrence.year();
    if (numMonths === 1) return LL.subscribe.recurrence.month();
    return '';
  }

  // Apple 3.1.2(c) per-plan trial disclosure assembler. Returns the
  // fully formatted line ("14 days free trial, then $19.99/month") when
  // both the trial period and the recurrence can be rendered; returns
  // '' otherwise. The '' return tells the template to hide the
  // disclosure rather than ship malformed copy on a load-bearing Apple-
  // compliance surface. Unrenderable cases are surfaced to Sentry so a
  // future product config that drops out of our known shape is visible.
  trialDisclosureFor(product: IAPProduct): string {
    if (!product.hasTrial || !product.trialPeriod) return '';
    const period = this.formatTrialPeriod(product.trialPeriod);
    const recurrence = this.recurrenceLabel(product.numMonths);
    if (!period || !recurrence) {
      this.services.logging.trackSilentProblem('SubscribeTrialDisclosureUnrenderable', {
        productId: product.id,
        trialPeriodRaw: product.trialPeriod,
        numMonths: product.numMonths,
        hasFormattedPeriod: !!period,
        hasRecurrence: !!recurrence,
      });
      return '';
    }
    return LL.subscribe.labels.trialDisclosure({
      period,
      price: product.priceText,
      recurrence,
    });
  }
}
